import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Activity, Wrench, Truck, History, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Diagnostic", icon: Activity, testid: "nav-diagnose" },
  { to: "/mechanics", label: "Mechanics", icon: Wrench, testid: "nav-mechanics" },
  { to: "/tow-trucks", label: "Towing", icon: Truck, testid: "nav-tow" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
];

export default function Layout() {
  const nav = useNavigate();
  return (
    <div className="relative min-h-screen bg-[#0B0F17] text-slate-100">
      <div className="tactical-grid fixed inset-0 opacity-70 pointer-events-none" />
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0F17]/85 border-b border-slate-800/80"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <NavLink
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-[0_0_20px_rgba(245,158,11,0.35)]">
              <Activity className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-extrabold text-xl uppercase tracking-tight text-slate-50">
                AutoPulse
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">
                AI · Diagnostics
              </span>
            </div>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-full p-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-amber-500 text-slate-950"
                      : "text-slate-300 hover:text-amber-400 hover:bg-slate-800/60"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Button
            data-testid="emergency-tow-btn"
            onClick={() => nav("/tow-trucks")}
            className="rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold gap-2 pulse-danger px-3 sm:px-5"
          >
            <ShieldAlert className="w-4 h-4" />
            <span className="hidden sm:inline">Emergency Tow</span>
            <span className="sm:hidden">SOS</span>
          </Button>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden border-t border-slate-800 bg-[#0B0F17]/95">
          <div className="grid grid-cols-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-3 text-[11px] font-medium transition-colors ${
                    isActive ? "text-amber-500" : "text-slate-400"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-slate-800/60 mt-16 py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between gap-4 text-xs text-slate-500">
          <div className="font-mono uppercase tracking-widest">
            AutoPulse AI · Powered by Claude Sonnet 5 + Whisper
          </div>
          <div className="font-mono">
            Diagnostic reference only — always confirm with a certified technician.
          </div>
        </div>
      </footer>
    </div>
  );
}
